
import { Col, Row, Spin } from "antd";
import { Power3, TimelineMax, TweenMax } from "gsap";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { EMAIL, SKILLS, TechSkills } from "../App.constant";
import carrotsuite from "../assets/images/carrotsuite.svg";
import demz from "../assets/images/demz_logo.png";
import etranzact from "../assets/images/etranzact_logo.png";
import fintrak from "../assets/images/fintrak.svg";
import hiit from "../assets/images/hiit.svg";
import inheaden from "../assets/images/inheaden_logo.png";
import mumspring from "../assets/images/mumspring.svg";
import stateWrapper from "../containers/provider";
import { useResponsive } from "../hooks/responsive";
import Svgs from "../svgs/index";

const Projects = lazy(() => import("./Projects"));

const Home = (props) => {
  const [
    isTabletOrMobileDevice,
    isTabletOrMobile,
    isBigScreen,
    isDesktopOrLaptop,
    isPortrait,
    isRetina,
    isTablet,
  ] = useResponsive();

  const [currentText, setCurrentText] = useState("VICTOR UMESIOBI");
  const [greetingText, setGreetingText] = useState("Hi there 👋, my name is");
  const [isCareer, setIsCareer] = useState(false);
  
  const logoRef = useRef(null);
  const textRef = useRef(null);
  const heroRef = useRef(null);
  const filterRef = useRef(null); // Ref for the SVG filter
  


  const playGlitchAnimation = () => {
    const tl = new TimelineMax();
    
    tl.to(filterRef.current, 0.5, { attr: { scale: 50 }, ease: Power3.easeIn }) // Distort IN
      .call(() => {
         setIsCareer(prevState => {
            const newState = !prevState;
            setCurrentText(newState ? "AI ENGINEER" : "VICTOR UMESIOBI");
            setGreetingText(newState ? "I am an" : "Hi there 👋, my name is");
            return newState;
         });
      })
      .to(filterRef.current, 0.5, { attr: { scale: 0 }, ease: Power3.easeOut }); // Distort OUT
      
    return tl;
  };

  const onMouseLeave = () => {
     // No-op
  };

  const onMouseEnter = () => {
     playGlitchAnimation();
  };

  useEffect(() => {
    // Initial fade in
    TweenMax.to(logoRef.current, 1, { opacity: 1, y: -20, ease: Power3.easeInOut }).play();
    TweenMax.to(textRef.current, 1, { opacity: 1, y: -20, ease: Power3.easeInOut, delay: 0.2 }).play();
    TweenMax.to(heroRef.current, 1, { opacity: 1, y: -20, ease: Power3.easeInOut, delay: 0.2 }).play();

    // Loop using setInterval for more control than Timeline repeat
    const interval = setInterval(() => {
        playGlitchAnimation();
    }, 6000); // Run every 6 seconds

    return () => {
      clearInterval(interval);
    };
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (window) {
      props?.masterStore?.setSelectedBar(location?.pathname);
    }
  }, [location?.pathname]);

  return (
    <div>
      <div style={{ width: "100%", minHeight: "100vh", marginTop: 0, paddingBottom: isTabletOrMobileDevice ? 120 : 200, display: "flex", flexDirection: "column", justifyContent: isTabletOrMobileDevice ? "flex-start" : "flex-start", overflowX: "hidden" }}>
        {/* SVG Filter Definition */}
        <svg style={{ position: "absolute", width: 0, height: 0 }}>
          <defs>
            <filter id="fluid-glitch">
              <feTurbulence 
                type="fractalNoise" 
                baseFrequency="0.02 0.1" 
                numOctaves="3" 
                result="warp" 
              />
              <feDisplacementMap 
                in="SourceGraphic" 
                in2="warp" 
                scale="0" 
                xChannelSelector="R" 
                yChannelSelector="G" 
                ref={filterRef}
              />
            </filter>
          </defs>
        </svg>

        {/* Logo - positioned at top left with proper spacing */}
        <div style={{ 
          paddingTop: isTabletOrMobileDevice ? 20 : 30, 
          paddingLeft: isTabletOrMobileDevice ? 20 : 50,
          marginBottom: isTabletOrMobileDevice ? 60 : 0
        }}>
            <div style={{
              fontFamily: "'Courier New', monospace",
              fontSize: isTabletOrMobileDevice ? 24 : 28,
              fontWeight: "bold",
              color: "#faf8ff",
              cursor: "pointer",
              letterSpacing: "-1px"
            }}>
              <span style={{ color: "#faf8ff" }}>&lt;</span>
              <span style={{ color: "#faf8ff" }}>VU</span>
              <span style={{ color: "#faf8ff" }}> /&gt;</span>
            </div>
        </div>

        <Row align="middle" style={{ paddingLeft: isTablet ? 40 : (isTabletOrMobileDevice ? 20 : 120), paddingRight: isTablet ? 40 : (isTabletOrMobileDevice ? 20 : 120), paddingTop: isTablet ? 60 : (isTabletOrMobileDevice ? 20 : 100), marginTop: isTablet ? 50 : (isTabletOrMobileDevice ? 60 : 50) }}>
          <Col span={24} ref={logoRef} style={{ opacity: 0 }}>
              {/* Greeting text - animated to sync with name/career */}
              <div
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: isTabletOrMobileDevice ? 16 : 20,
                  color: "#B0B6C5",
                  marginBottom: isTabletOrMobileDevice ? 15 : 20,
                  transition: "opacity 0.3s ease",
                  minHeight: isTabletOrMobileDevice ? 24 : 30
                }}
              >
                {greetingText}
              </div>
              
              {/* Name with animation */}
              <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    overflow: "visible",
                    position: "relative",
                    marginBottom: isTabletOrMobileDevice ? 30 : 40,
                    minHeight: isTabletOrMobileDevice ? 150 : 100 // Added minHeight to prevent jitter
                }}
              >
                  <div
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: "900",
                      fontSize: isTabletOrMobileDevice ? "clamp(32px, 10vw, 50px)" : "clamp(40px, 5vw, 80px)",
                      color: isCareer ? "#33CC99" : "#FFFFFF", // White for name, green for career
                      textTransform: "uppercase",
                      letterSpacing: "-2px",
                      filter: "url(#fluid-glitch)",
                      transition: "color 0.1s ease",
                      willChange: "filter, color",
                      position: "relative",
                      display: "inline-block",
                      cursor: "pointer",
                      lineHeight: 1.1
                    }}
                  >
                    {currentText}
                    {/* Anchor span to ensure ZigZag flows with the text end */}
                    <span style={{ position: "relative", display: "inline-block", width: 0, height: 0 }}>
                        <div style={{ 
                          position: "absolute", 
                          bottom: isTabletOrMobileDevice ? -40 : -50,
                          right: isTablet ? -50 : (isTabletOrMobileDevice ? -20 : -80), 
                          zIndex: 50,
                          width: "max-content" // Ensure div doesn't collapse
                        }}>
                            <Svgs.Zigzaga fill="#ffad0d" width={isTablet ? 150 : (isTabletOrMobileDevice ? 100 : 200)} height={isTablet ? 60 : (isTabletOrMobileDevice ? 50 : 80)} />
                        </div>
                    </span>
                  </div>
              </div>
          </Col>
          <Col span={24}>
            <div
              ref={heroRef}
              style={{
                opacity: 0,
                position: "relative",
                top: isTabletOrMobile ? 0 : 0
              }}
            >
              <div
                style={{
                  width: isTabletOrMobileDevice ? "95%" : "100%",
                  maxWidth: "750px",
                  padding: 0,
                  height: "auto",
                  paddingBottom: 20,
                  textAlign: "left",
                  margin: isTabletOrMobileDevice ? "30px auto 10px auto" : "50px 0 10px 0" // Center on mobile
                }}
              >
                <div
                  style={{
                    fontSize: isTabletOrMobileDevice ? 16 : 20,
                    fontWeight: "400",
                    fontFamily: "'Montserrat', sans-serif",
                    color: "#B0B6C5", // Light grey/blue for "airy" look
                    lineHeight: "1.8",
                    maxWidth: "750px",
                    margin: "0"
                  }}
                >
                      With over <b style={{ color: "#FFF" }}>7 years</b> of software development experience, I have
                      built a wide range of products across web, mobile, and desktop
                      platforms. I hold a Master’s degree in Artificial Intelligence
                      from Teesside University, UK, where I specialized in
                      Reinforcement Learning, Machine Learning, RAG, and Deep
                      Learning. I am also a published researcher, with my work
                      featured in IEEE Xplore:{" "}
                      <a
                        href="https://ieeexplore.ieee.org/document/10911886"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#33CC99", textDecoration: "underline", fontWeight: "bold" }}
                      >
                        IEEE Xplore
                      </a>
                      . I am self-motivated, constantly learning, and dedicated to
                      delivering excellence with a unique problem-solving perspective.
                      Thank you.
                    </div>
                    <div style={{ marginTop: isTabletOrMobileDevice ? 40 : 30 }}>
                      <a
                        href={`mailto:${EMAIL}`}
                        style={{
                          display: "block",
                          width: "100%",
                          maxWidth: isTabletOrMobileDevice ? "100%" : "400px",
                          padding: isTabletOrMobileDevice ? "18px 0" : "18px 0",
                          backgroundColor: "#33CC99",
                          color: "#FFF",
                          borderRadius: "10px",
                          fontWeight: "bold",
                          fontSize: isTabletOrMobileDevice ? 16 : 18,
                          textDecoration: "none",
                          boxShadow: "0 10px 20px rgba(51, 204, 153, 0.3)",
                          fontFamily: "'Montserrat', sans-serif",
                          textAlign: "center"
                        }}
                        className="hover"
                      >
                        Hire Me
                      </a>
                    </div>
              </div>
            </div>
          </Col>

          {/* New Sections: Companies, Skills, Tech - Moved from About.js */}
          <Col span={24}>
             <center>
                <div
                  style={{
                    color: "#33CC99",
                    padding: 10,
                    marginTop: isTabletOrMobileDevice ? 80 : 150,
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: "bold",
                    letterSpacing: "4px",
                    textTransform: "uppercase",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Companies I have worked with
                </div>
              <div
                style={{
                  width: isTabletOrMobileDevice ? "90%" : "80%",
                  padding: isTabletOrMobileDevice ? 10 : 40,
                  marginTop: 20,
                  margin: "20px auto 0 auto"
                }}
              >
                <Row gutter={[16, 16]} align="middle" justify="center">
                  {[
                    { name: "fintrak", src: fintrak, url: "https://www.fintraksoftware.com/" },
                    { name: "inheaden", src: inheaden, url: "https://inheaden.io" },
                    { name: "mumspring", src: mumspring, url: "https://www.mumspring.com/" },
                    { name: "demz", src: demz, url: "https://demzanalytics.com" },
                    { name: "etranzact", src: etranzact, url: "https://www.etranzact.com/" },
                    { name: "carrotsuite", src: carrotsuite, url: "https://carrotsuite.com.ng/" },
                    { name: "hiit", src: hiit, url: "https://www.hiitplc.com/" }
                  ].map((item) => (
                    <Col key={item.name} xs={12} sm={8} md={6}>
                      <a target="_blank" referrerPolicy="no-referrer" href={item.url}>
                        <div 
                          className="glass-light hover-logo"
                          style={{
                              padding: "15px",
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: 80,
                              background: "rgba(255, 255, 255, 0.05)" // Subtle background to contain logos
                          }}
                        >
                            <img
                              src={item.src}
                              style={{ 
                                maxHeight: "100%", 
                                maxWidth: "100%",
                                width: "auto",
                                // Invert colors to make dark logos white, keep colored ones readable or grayscale until hover
                                filter: "grayscale(100%) brightness(0.8)", 
                                transition: "all 0.4s ease",
                                objectFit: "contain"
                              }}
                              onMouseOver={(e) => e.currentTarget.style.filter = "none"}
                              onMouseOut={(e) => e.currentTarget.style.filter = "grayscale(100%) brightness(0.8)"}
                            />
                        </div>
                      </a>
                    </Col>
                  ))}
                </Row>
              </div>
            </center>
             <center>
                <div
                  style={{
                    color: "#FFFFFF",
                    padding: 10,
                    marginTop: isTabletOrMobileDevice ? 20 : 10,
                    textAlign: "center",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontSize: 16
                  }}
                >
                  My Specialities
                </div>
                <div
                  style={{
                    width: isTabletOrMobileDevice ? "95%" : "80%",
                    padding: 10,
                    marginTop: 10,
                    marginBottom: 10,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "15px"
                  }}
                >
                  {SKILLS.map((i) => {
                    return (
                        <span
                          style={{
                            whiteSpace: "pre-wrap",
                            fontWeight: "500",
                            color: "#FFFFFF",
                            fontFamily: "'Montserrat', sans-serif",
                            padding: "12px 24px",
                            borderRadius: "50px",
                            border: "1px solid #33CC99",
                            background: "rgba(51, 204, 153, 0.05)",
                            letterSpacing: "1px",
                            fontSize: 14,
                            cursor: "default",
                            boxShadow: "0 0 10px rgba(51, 204, 153, 0.1)"
                          }}
                          className="hover-logo" // Reusing hover scale effect
                        >
                          {i}
                        </span>
                    );
                  })}
                </div>
              </center>

              <center>
                <div
                  style={{
                    color: "#FFFFFF",
                    padding: 10,
                    marginTop: isTabletOrMobileDevice ? 20 : 10,
                    textAlign: "center",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontSize: 16
                  }}
                >
                  Technologies I use
                </div>
                <div
                  style={{
                     width: isTabletOrMobileDevice ? "95%" : "80%",
                    padding: 10,
                    marginTop: 10,
                    marginBottom: 10,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px"
                  }}
                >
                  {TechSkills.map((i) => {
                    return (
                      <div style={{ display: "inline-block", margin: "5px" }}>
                        <span
                          style={{
                            whiteSpace: "pre-wrap",
                            fontWeight: "400",
                            color: "#ccc",
                            fontFamily: "'Courier New', monospace", // Techy font
                            padding: "8px 16px",
                            borderRadius: "4px",
                            border: "1px solid rgba(51, 204, 153, 0.2)",
                            fontSize: 14,
                            background: "rgba(0,0,0,0.3)"
                          }}
                          className="hover-logo"
                        >
                          {i}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </center>
          </Col>

          {isTabletOrMobileDevice ? (
            <Col span={24} style={{ padding: 20 }}>
              <div
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: "#33CC99",
                  fontSize: 24,
                  fontWeight: "900",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  textAlign: "center",
                  marginBottom: 20,
                  marginTop: 20
                }}
              >
                 Projects
              </div>
              <Suspense
                fallback={
                  <center>
                    <div className="spin">
                      <Spin />
                    </div>
                  </center>
                }
              >
                <Projects
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              </Suspense>
            </Col>
          ) : (
            <Col span={24} style={{ paddingLeft: "15%", paddingRight: "15%" }}>
              <div
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: "#33CC99",
                  fontSize: 30,
                  fontWeight: "900",
                  letterSpacing: "5px",
                  textTransform: "uppercase",
                  textAlign: "center",
                  marginBottom: 30,
                  marginTop: 30
                }}
              >
              Projects
              </div>
              <Suspense
                fallback={
                  <center>
                    <div className="spin">
                      <Spin />
                    </div>
                  </center>
                }
              >
                <Projects
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              </Suspense>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default stateWrapper(Home);
